/**
 * Hook to get the current user's team from team_members table
 * For CARA Compliance v1 MVP: assumes user has one primary team
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

interface TeamMembership {
  id: string;
  team_id: string;
  profile_id: string;
  role: 'owner' | 'coach' | 'assistant_coach' | 'analyst';
  is_owner: boolean;
  team: {
    id: string;
    name: string;
    sport: string;
    division: string | null;
    gender: string | null;
    current_season: string;
  };
}

/**
 * Fetches the current user's primary team
 * Returns the first team where user is a coach/owner
 */
export function useCurrentTeam() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['current-team', user?.id],
    queryFn: async (): Promise<TeamMembership | null> => {
      if (!user?.id) {
        return null;
      }

      const { data, error } = await supabase
        .from('team_members')
        .select(
          `
          id,
          team_id,
          profile_id,
          role,
          is_owner,
          created_at,
          team:teams(
            id,
            name,
            sport,
            division,
            gender,
            current_season,
            created_at
          )
        `
        )
        .eq('profile_id', user.id)
        .in('role', ['owner', 'coach', 'assistant_coach', 'head_coach'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        // No team found is not an error - return null
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data as unknown as TeamMembership;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
